import { useSimulaData } from '../hooks/useSimulaData';
import { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area,
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertTriangle, Users, Award, CheckCircle,
  Upload, UserCircle, ClipboardList, FileBarChart, Bell, Clock,
  ArrowRight, Eye, FileText, MessageSquare, ChevronRight,
  Minus, AlertCircle, Shield, Sparkles, StickyNote, Building2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ApproveAuditModal } from '../components/supervisor/ApproveAuditModal';
import { AddOperatorModal } from '../components/supervisor/AddOperatorModal';
import { NotifyTeamModal } from '../components/supervisor/NotifyTeamModal';
import { isConcludedAuditStatus } from '../utils/auditStatus';

export function SupervisorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    operators: opEntity, supervisors: supEntity, sectors: secEntity,
    monitors: monEntity, audits: audEntity, trainingSessions: trEntity,
    scorecard: scorecardEntity, pendingApprovals: paEntity,
    operatorStats, getSupervisor, getMonitor,
    getAlertsBySupervisor, getPendingApprovalsBySupervisor,
    getWeeklyTrendsBySupervisor, getActivitiesBySupervisor, getCoachingNotesBySupervisor,
    coachingNotes: coachingNotesEntity,
  } = useSimulaData();
  const operators = opEntity.data;
  const supervisors = supEntity.data;
  const sectors = secEntity.data;
  const monitors = monEntity.data;
  const audits = audEntity.data;
  const trainingSessions = trEntity.data;
  const scorecard = scorecardEntity.data;
  const [selectedAudit, setSelectedAudit] = useState<any>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showAddOperatorModal, setShowAddOperatorModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ operatorId: '', category: 'feedback', note: '', followUpDate: '' });
  const [activeTab, setActiveTab] = useState<'overview' | 'alerts' | 'approvals' | 'coaching'>('overview');

  // Para admin/qualidade, permite selecionar qual supervisor visualizar
  const isAdminOrQualidade = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'qualidade';
  const canReviewApprovals = user?.role === 'admin' || user?.role === 'supervisor';
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>(
    isAdminOrQualidade ? (supervisors[0]?.id || '') : (user?.id || '')
  );
  const effectiveSupervisorId = isAdminOrQualidade ? selectedSupervisorId : (user?.id || '');
  const currentSupervisor = getSupervisor(effectiveSupervisorId);
  const currentSectorName = sectors.find(s => s.id === currentSupervisor?.sectorId)?.name || '';

  // Filtrar operadores do supervisor
  const myOperators = operators.filter((op) => op.supervisorId === effectiveSupervisorId);
  const myOperatorIds = myOperators.map((op) => op.id);

  // Calcular estatísticas do time
  const teamStats = myOperators.map((op) => {
    const stats = operatorStats[op.id as keyof typeof operatorStats];
    return { operator: op, ...stats };
  });

  const avgTeamTraining = teamStats.length > 0
    ? teamStats.reduce((sum, s) => sum + s.avgTrainingScore, 0) / teamStats.length
    : 0;
  const avgTeamAudit = teamStats.length > 0
    ? teamStats.reduce((sum, s) => sum + s.avgAuditScore, 0) / teamStats.length
    : 0;
  const totalZeroPercentage = teamStats.length > 0
    ? teamStats.reduce((sum, s) => sum + s.zeroScorePercentage, 0) / teamStats.length
    : 0;
  const totalFalhaGrave = teamStats.reduce((sum, s) => sum + s.falhaGraveCount, 0);

  // Top performers
  const topPerformers = [...teamStats]
    .map((s) => ({
      ...s,
      avgTotal: (s.avgTrainingScore + s.avgAuditScore) / 2,
    }))
    .sort((a, b) => b.avgTotal - a.avgTotal);

  // Top 3 falhas graves mais frequentes
  const allFailures: { [key: string]: number } = {};
  [...audits, ...trainingSessions]
    .filter((item) => myOperatorIds.includes(item.operatorId))
    .filter((item) => {
      if ('status' in item) {
        return isConcludedAuditStatus(item.status) && Boolean(item.evaluation);
      }
      return Boolean(item.evaluation);
    })
    .forEach((item) => {
      if (item.evaluation?.hasFalhaGrave) {
        item.evaluation.items.forEach((evalItem) => {
          if (!evalItem.passed) {
            allFailures[evalItem.itemId] = (allFailures[evalItem.itemId] || 0) + 1;
          }
        });
      }
    });

  const scorecardMap = useMemo(
    () => Object.fromEntries(scorecard.map((item) => [item.id, item])),
    [scorecard],
  );

  const topFailures = Object.entries(allFailures)
    .map(([itemId, count]) => ({
      itemId,
      count,
      name: scorecardMap[itemId]?.name ?? 'Item desconhecido',
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  // Dados do supervisor
  const myAlerts = getAlertsBySupervisor(effectiveSupervisorId);
  const unreadAlerts = myAlerts.filter(a => !a.isRead);
  const myPendingApprovals = getPendingApprovalsBySupervisor(effectiveSupervisorId);
  const myWeeklyTrends = getWeeklyTrendsBySupervisor(effectiveSupervisorId);
  const myActivities = getActivitiesBySupervisor(effectiveSupervisorId);
  const myCoachingNotes = getCoachingNotesBySupervisor(effectiveSupervisorId);

  // Ranking chart data
  const rankingData = topPerformers.map((p) => ({
    name: p.operator.name.split(' ')[0],
    treino: p.avgTrainingScore,
    auditoria: p.avgAuditScore,
    media: Math.round((p.avgTrainingScore + p.avgAuditScore) / 2),
  }));

  // Weekly trend chart
  const trendChartData = myWeeklyTrends.map(t => ({
    name: t.weekLabel.replace('Sem ', 'S'),
    score: t.avgScore,
    auditorias: t.totalAudits,
    treinos: t.totalTrainings,
    falhas: t.falhaGraveCount,
  }));

  const handleApproval = (approval: typeof myPendingApprovals[0]) => {
    setSelectedAudit({
      id: approval.auditId,
      approvalId: approval.id,
      operatorName: approval.operatorName,
      date: approval.date,
      score: approval.score,
      duration: Math.round(approval.duration / 60),
    });
    setShowApproveModal(true);
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { bg: 'var(--error-muted)', border: 'var(--error)', icon: 'var(--error)' };
      case 'warning':
        return { bg: 'var(--destructive-muted)', border: 'var(--warning)', icon: 'var(--warning)' };
      case 'success':
        return { bg: 'color-mix(in srgb, var(--success) 8%, transparent)', border: 'var(--success)', icon: 'var(--success)' };
      default:
        return { bg: 'color-mix(in srgb, var(--primary) 8%, transparent)', border: 'var(--primary)', icon: 'var(--primary)' };
    }
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp size={16} style={{ color: 'var(--success)' }} />;
    if (trend === 'down') return <TrendingDown size={16} style={{ color: 'var(--error)' }} />;
    return <Minus size={16} style={{ color: 'var(--muted-foreground)' }} />;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'audit_completed': return <FileText size={16} />;
      case 'training_completed': return <Sparkles size={16} />;
      case 'falha_grave': return <AlertTriangle size={16} />;
      case 'badge_earned': return <Award size={16} />;
      case 'goal_completed': return <CheckCircle size={16} />;
      case 'certificate_earned': return <Shield size={16} />;
      case 'feedback_received': return <MessageSquare size={16} />;
      default: return <Bell size={16} />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'falha_grave': return 'var(--error)';
      case 'badge_earned': case 'certificate_earned': case 'goal_completed': return 'var(--success)';
      case 'audit_completed': return 'var(--chart-1)';
      case 'training_completed': return 'var(--chart-2)';
      default: return 'var(--muted-foreground)';
    }
  };

  const tabs = [
    { id: 'overview' as const, label: 'Visao Geral', icon: Users },
    { id: 'alerts' as const, label: `Alertas (${unreadAlerts.length})`, icon: Bell },
    { id: 'approvals' as const, label: `Aprovacoes (${myPendingApprovals.length})`, icon: CheckCircle },
    { id: 'coaching' as const, label: 'Coaching', icon: StickyNote },
  ];

  return (
    <div style={{ fontFamily: 'var(--font-family-inter)' }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)' }}>
              Dashboard do Supervisor
            </h1>
            <p style={{ color: 'var(--muted-foreground)', marginTop: '4px', fontSize: 'var(--text-sm)' }}>
              {isAdminOrQualidade
                ? `Visualizando: ${currentSupervisor?.name || 'Supervisor'} — ${currentSectorName}`
                : 'Acompanhe o desempenho do seu time em tempo real'}
            </p>
          </div>
          {/* Seletor de Supervisor para admin/qualidade */}
          {isAdminOrQualidade && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="supervisor-select"
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--muted-foreground)',
                }}
              >
                Supervisor:
              </label>
              <select
                id="supervisor-select"
                value={selectedSupervisorId}
                onChange={(e) => setSelectedSupervisorId(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-input)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--input-background)',
                  color: 'var(--foreground)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                  cursor: 'pointer',
                  minWidth: '200px',
                }}
              >
                {supervisors.map((sup) => (
                  <option key={sup.id} value={sup.id}>
                    {sup.name} — {sectors.find(s => s.id === sup.sectorId)?.name || ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div
        className="mb-6 p-5"
        style={{
          backgroundColor: 'var(--card)',
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border)',
        }}
      >
        <h4 className="mb-3 flex items-center gap-2" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}>
          <CheckCircle size={18} style={{ color: 'var(--primary)' }} />
          Acoes Rapidas
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Aprovar Auditorias', icon: CheckCircle, color: 'var(--chart-1)', href: '/auditorias' },
            { label: 'Ver Operadores', icon: UserCircle, color: 'var(--chart-3)', href: '/operadores' },
            { label: 'Questionarios', icon: ClipboardList, color: 'var(--chart-4)', href: '/questionarios' },
            { label: 'Exportar Relatorios', icon: FileBarChart, color: 'var(--chart-5)', href: '/relatorios' },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.href)}
              style={{
                padding: '12px',
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--border)',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                fontFamily: 'var(--font-family-inter)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--muted)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <action.icon size={22} style={{ color: action.color }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--foreground)' }}>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Operadores', value: myOperators.length, icon: Users, color: 'var(--primary)', testId: 'supervisor-kpi-operators' },
          { label: 'Media Treino', value: avgTeamTraining > 0 ? avgTeamTraining.toFixed(0) : '--', icon: TrendingUp, color: 'var(--chart-1)', testId: 'supervisor-kpi-avg-training' },
          { label: 'Media Auditoria', value: avgTeamAudit > 0 ? avgTeamAudit.toFixed(0) : '--', icon: TrendingUp, color: 'var(--chart-2)', testId: 'supervisor-kpi-avg-audit' },
          { label: '% Zeradas', value: `${totalZeroPercentage.toFixed(0)}%`, icon: AlertTriangle, color: totalZeroPercentage > 0 ? 'var(--error)' : 'var(--chart-1)', valueColor: totalZeroPercentage > 0 ? 'var(--error)' : undefined, testId: 'supervisor-kpi-zero-rate' },
          { label: 'Falhas Graves', value: totalFalhaGrave, icon: Shield, color: totalFalhaGrave > 0 ? 'var(--error)' : 'var(--success)', valueColor: totalFalhaGrave > 0 ? 'var(--error)' : 'var(--success)', testId: 'supervisor-kpi-falhas-graves' },
        ].map((stat) => (
          <div
            key={stat.label}
            data-testid={stat.testId}
            className="p-4"
            style={{
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontWeight: 'var(--font-weight-medium)' }}>
                {stat.label}
              </p>
              <stat.icon size={18} style={{ color: stat.color }} />
            </div>
            <p
              data-testid={`${stat.testId}-value`}
              style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-semibold)',
              color: (stat as any).valueColor || 'var(--foreground)',
            }}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 p-1"
        style={{
          backgroundColor: 'var(--muted)',
          borderRadius: 'var(--radius-button)',
          width: 'fit-content',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2 transition-all"
            style={{
              borderRadius: 'var(--radius-button)',
              border: 'none',
              backgroundColor: activeTab === tab.id ? 'var(--card)' : 'transparent',
              color: activeTab === tab.id ? 'var(--foreground)' : 'var(--muted-foreground)',
              fontWeight: activeTab === tab.id ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-inter)',
              boxShadow: activeTab === tab.id ? 'var(--elevation-sm)' : 'none',
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Overview */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Ranking por Operador (Grouped Bar) */}
            <div
              className="p-5"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '16px' }}>
                Desempenho por Operador
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rankingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }} style={{ fontFamily: 'var(--font-family-inter)' }} />
                  <YAxis stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }} style={{ fontFamily: 'var(--font-family-inter)' }} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-family-inter)' }}
                    labelStyle={{ color: 'var(--popover-foreground)', fontFamily: 'var(--font-family-inter)' }}
                    itemStyle={{ fontFamily: 'var(--font-family-inter)' }}
                  />
                  <Legend wrapperStyle={{ fontFamily: 'var(--font-family-inter)', fontSize: 12 }} />
                  <Bar dataKey="treino" fill="var(--chart-2)" name="Treino" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="auditoria" fill="var(--chart-1)" name="Auditoria" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Tendencia Semanal (Area) */}
            <div
              className="p-5"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '16px' }}>
                Tendencia Semanal do Time
              </h3>
              {trendChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} style={{ fontFamily: 'var(--font-family-inter)' }} />
                    <YAxis stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }} style={{ fontFamily: 'var(--font-family-inter)' }} domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-family-inter)' }}
                      labelStyle={{ color: 'var(--popover-foreground)' }}
                      itemStyle={{ fontFamily: 'var(--font-family-inter)' }}
                    />
                    <Legend wrapperStyle={{ fontFamily: 'var(--font-family-inter)', fontSize: 12 }} />
                    <Area type="monotone" dataKey="score" stroke="var(--chart-1)" fill="color-mix(in srgb, var(--chart-1) 15%, transparent)" name="Score Medio" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center" style={{ height: 280, color: 'var(--muted-foreground)' }}>
                  Sem dados de tendencia
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Falhas Graves */}
            <div
              className="p-5"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '16px' }}>
                Top Falhas Graves do Time
              </h3>
              <div className="space-y-3">
                {topFailures.length === 0 ? (
                  <p style={{ color: 'var(--muted-foreground)', textAlign: 'center', padding: '32px', fontSize: 'var(--text-sm)' }}>
                    Nenhuma falha grave registrada!
                  </p>
                ) : (
                  topFailures.map((failure, index) => (
                    <div
                      key={failure.itemId}
                      className="flex items-center justify-between p-3"
                      style={{ backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)' }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 flex items-center justify-center"
                          style={{
                            backgroundColor: 'var(--error)',
                            color: 'var(--error-foreground)',
                            borderRadius: '50%',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-weight-semibold)',
                          }}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <p style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>{failure.name}</p>
                          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Ocorrencias no time</p>
                        </div>
                      </div>
                      <span
                        className="px-3 py-1"
                        style={{
                          backgroundColor: 'var(--error)',
                          color: 'var(--error-foreground)',
                          borderRadius: 'var(--radius)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                        }}
                      >
                        {failure.count}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Atividades Recentes */}
            <div
              className="p-5"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '16px' }}>
                Atividades Recentes
              </h3>
              <div className="space-y-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {myActivities.slice(0, 8).map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-3"
                    style={{ backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)' }}
                  >
                    <div
                      className="flex items-center justify-center mt-0.5"
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: `color-mix(in srgb, ${getActivityColor(activity.type || '')} 15%, transparent)`,
                        color: getActivityColor(activity.type || ''),
                        flexShrink: 0,
                      }}
                    >
                      {getActivityIcon(activity.type || '')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                        {activity.operatorName}
                      </p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginTop: '2px' }}>
                        {activity.description}
                      </p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginTop: '4px' }}>
                        {new Date(activity.date).toLocaleDateString('pt-BR')} - {new Date(activity.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {activity.score !== undefined && (
                      <span
                        className="px-2 py-1"
                        style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-semibold)',
                          borderRadius: 'var(--radius)',
                          backgroundColor: activity.score === 0 ? 'var(--error)' : activity.score >= 80 ? 'var(--success)' : 'var(--warning)',
                          color: 'var(--primary-foreground)',
                          flexShrink: 0,
                        }}
                      >
                        {activity.score}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Visao Geral do Setor */}
          {!isAdminOrQualidade && currentSupervisor?.sectorId && (() => {
            const sectorOps = operators.filter(op => op.sectorId === currentSupervisor.sectorId);
            const sectorSups = supervisors.filter(s => s.sectorId === currentSupervisor.sectorId);
            const sectorAudits = audits.filter(a => a.sectorId === currentSupervisor.sectorId && isConcludedAuditStatus(a.status) && a.evaluation);
            const sectorAvgScore = sectorAudits.length > 0
              ? Math.round(sectorAudits.reduce((sum, a) => sum + (a.evaluation?.score || 0), 0) / sectorAudits.length)
              : 0;
            const sectorFG = sectorAudits.filter(a => a.evaluation?.hasFalhaGrave).length;

            return (
              <div
                className="p-5 mb-6"
                style={{
                  backgroundColor: 'var(--card)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={20} style={{ color: 'var(--secondary)' }} />
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Visao Geral do Setor: {currentSectorName}
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[
                    { label: 'Total Operadores', value: sectorOps.length, color: 'var(--primary)' },
                    { label: 'Supervisores', value: sectorSups.length, color: 'var(--secondary)' },
                    { label: 'Score Medio Setor', value: sectorAvgScore, color: sectorAvgScore >= 70 ? 'var(--success)' : 'var(--warning)' },
                    { label: 'Falhas Graves Setor', value: sectorFG, color: sectorFG > 0 ? 'var(--error)' : 'var(--success)' },
                  ].map((item) => (
                    <div key={item.label} className="p-3" style={{ backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)' }}>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '4px' }}>{item.label}</p>
                      <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', color: item.color, fontFamily: 'var(--font-family-mono)' }}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
                <div>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '8px' }}>Supervisores do Setor:</p>
                  <div className="flex flex-wrap gap-2">
                    {sectorSups.map(s => (
                      <span
                        key={s.id}
                        className="flex items-center gap-1.5 px-3 py-1.5"
                        style={{
                          backgroundColor: s.id === effectiveSupervisorId ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'var(--muted)',
                          color: s.id === effectiveSupervisorId ? 'var(--primary)' : 'var(--foreground)',
                          borderRadius: 'var(--radius-button)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: s.id === effectiveSupervisorId ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
                          border: s.id === effectiveSupervisorId ? '1px solid var(--primary)' : '1px solid var(--border)',
                        }}
                      >
                        {s.name}
                        {s.id === effectiveSupervisorId && <span style={{ fontSize: 'var(--text-micro)' }}>(Voce)</span>}
                        <span style={{ color: 'var(--muted-foreground)' }}>
                          ({operators.filter(op => op.supervisorId === s.id).length} ops)
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Lista de Operadores */}
          <div
            style={{
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)' }}>Meus Operadores</h3>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {topPerformers.map((perf, index) => (
                <Link
                  key={perf.operator.id}
                  to={`/operadores/${perf.operator.id}`}
                  className="flex items-center justify-between p-4 transition-colors"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--muted)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div className="flex items-center gap-3">
                    {index < 3 && (
                      <Award size={20} style={{ color: index === 0 ? 'var(--chart-3)' : index === 1 ? 'var(--muted-foreground)' : 'var(--chart-3)' }} />
                    )}
                    <div
                      className="w-10 h-10 flex items-center justify-center"
                      style={{
                        backgroundColor: 'var(--primary)',
                        borderRadius: '50%',
                        color: 'var(--primary-foreground)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                      }}
                    >
                      {perf.operator.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>{perf.operator.name}</p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                        {sectors.find(s => s.id === perf.operator.sectorId)?.name || ''} - {perf.totalSessions} sessoes
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-right">
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Media</p>
                      <div className="flex items-center gap-1">
                        <p style={{
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: perf.avgTotal >= 70 ? 'var(--chart-1)' : 'var(--foreground)',
                        }}>
                          {perf.avgTotal.toFixed(0)}
                        </p>
                        {getTrendIcon(perf.trend)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Zeradas</p>
                      <p style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: perf.zeroScorePercentage > 0 ? 'var(--error)' : 'var(--success)',
                      }}>
                        {perf.zeroScorePercentage}%
                      </p>
                    </div>
                    <ChevronRight size={18} style={{ color: 'var(--muted-foreground)' }} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}

      {/* TAB: Alertas */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {myAlerts.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <CheckCircle size={48} style={{ color: 'var(--success)', margin: '0 auto 16px' }} />
              <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)' }}>Nenhum alerta</p>
              <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', marginTop: '4px' }}>Seu time esta operando normalmente.</p>
            </div>
          ) : (
            myAlerts.map((alert) => {
              const styles = getSeverityStyles(alert.severity);
              return (
                <div
                  key={alert.id}
                  className="flex items-start gap-4 p-4"
                  style={{
                    backgroundColor: styles.bg,
                    borderRadius: 'var(--radius-card)',
                    borderLeft: `4px solid ${styles.border}`,
                    opacity: alert.isRead ? 0.7 : 1,
                  }}
                >
                  <div
                    className="flex items-center justify-center mt-0.5"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: `color-mix(in srgb, ${styles.icon} 15%, transparent)`,
                      color: styles.icon,
                      flexShrink: 0,
                    }}
                  >
                    {alert.severity === 'critical' ? <AlertCircle size={18} /> :
                     alert.severity === 'warning' ? <AlertTriangle size={18} /> :
                     alert.severity === 'success' ? <Sparkles size={18} /> :
                     <Bell size={18} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="flex items-center gap-2 mb-1">
                      <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>{alert.title}</p>
                      {!alert.isRead && (
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: styles.icon,
                          display: 'inline-block',
                        }} />
                      )}
                    </div>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginBottom: '8px' }}>{alert.message}</p>
                    <div className="flex items-center gap-4">
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                        <Clock size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                        {new Date(alert.date).toLocaleDateString('pt-BR')} {new Date(alert.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {alert.actionUrl && (
                        <Link
                          to={alert.actionUrl}
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: styles.icon,
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {alert.actionLabel} <ArrowRight size={12} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* TAB: Aprovacoes */}
      {activeTab === 'approvals' && (
        <div className="space-y-3">
          {myPendingApprovals.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <CheckCircle size={48} style={{ color: 'var(--success)', margin: '0 auto 16px' }} />
              <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)' }}>Nenhuma aprovacao pendente</p>
              <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', marginTop: '4px' }}>Todas as auditorias foram revisadas.</p>
            </div>
          ) : (
            myPendingApprovals.map((approval) => (
              <div
                key={approval.id}
                className="flex items-center justify-between p-4"
                style={{
                  backgroundColor: 'var(--card)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                  borderLeft: approval.hasFalhaGrave ? '4px solid var(--error)' : '4px solid var(--border)',
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center"
                    style={{
                      backgroundColor: approval.hasFalhaGrave ? 'var(--error-muted)' : 'color-mix(in srgb, var(--primary) 10%, transparent)',
                      borderRadius: '50%',
                      color: approval.hasFalhaGrave ? 'var(--error)' : 'var(--primary)',
                    }}
                  >
                    <FileText size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>{approval.operatorName}</p>
                      <span
                        className="px-2 py-0.5"
                        style={{
                          fontSize: 'var(--text-xs)',
                          borderRadius: 'var(--radius)',
                          backgroundColor: approval.status === 'contested' ? 'var(--warning)' : approval.status === 'pending_feedback' ? 'var(--chart-2)' : 'var(--primary)',
                          color: 'var(--primary-foreground)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        {approval.status === 'contested' ? 'Contestada' : approval.status === 'pending_feedback' ? 'Aguardando Feedback' : 'Pendente'}
                      </span>
                    </div>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginTop: '2px' }}>
                      {approval.callId} - {new Date(approval.date).toLocaleDateString('pt-BR')} - {Math.round(approval.duration / 60)} min
                      {approval.monitorType === 'ai' ? ' (IA)' : ' (Humano)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Score</p>
                    <p style={{
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: approval.score === 0 ? 'var(--error)' : approval.score >= 80 ? 'var(--success)' : 'var(--warning)',
                    }}>
                      {approval.score}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/auditorias/${approval.auditId}`)}
                      className="px-3 py-1.5 flex items-center gap-1"
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-button)',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: 'var(--text-xs)',
                        fontFamily: 'var(--font-family-inter)',
                        color: 'var(--foreground)',
                      }}
                    >
                      <Eye size={14} /> Ver
                    </button>
                    {canReviewApprovals && (
                      <button
                        onClick={() => handleApproval(approval)}
                        className="px-3 py-1.5 flex items-center gap-1"
                        style={{
                          border: 'none',
                          borderRadius: 'var(--radius-button)',
                          backgroundColor: 'var(--primary)',
                          color: 'var(--primary-foreground)',
                          cursor: 'pointer',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          fontFamily: 'var(--font-family-inter)',
                        }}
                      >
                        <CheckCircle size={14} /> Revisar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* TAB: Coaching */}
      {activeTab === 'coaching' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)' }}>Notas de Coaching</h3>
            <button
              onClick={() => { setNoteForm({ operatorId: myOperators[0]?.id || '', category: 'feedback', note: '', followUpDate: '' }); setShowNoteModal(true); }}
              className="px-3 py-1.5 flex items-center gap-1"
              style={{
                border: 'none',
                borderRadius: 'var(--radius-button)',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                fontFamily: 'var(--font-family-inter)',
              }}
            >
              <StickyNote size={14} /> Nova Nota
            </button>
          </div>

          {myCoachingNotes.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)',
              }}
            >
              <StickyNote size={48} style={{ color: 'var(--muted-foreground)', margin: '0 auto 16px' }} />
              <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)' }}>Nenhuma nota de coaching</p>
              <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', marginTop: '4px' }}>Adicione notas para acompanhar o desenvolvimento do time.</p>
            </div>
          ) : (
            myCoachingNotes.map((note) => {
              const categoryStyles: Record<string, { bg: string; border: string; label: string }> = {
                warning: { bg: 'var(--error-muted)', border: 'var(--error)', label: 'Advertencia' },
                action_plan: { bg: 'var(--destructive-muted)', border: 'var(--warning)', label: 'Plano de Acao' },
                recognition: { bg: 'color-mix(in srgb, var(--success) 8%, transparent)', border: 'var(--success)', label: 'Reconhecimento' },
                feedback: { bg: 'color-mix(in srgb, var(--primary) 8%, transparent)', border: 'var(--primary)', label: 'Feedback' },
              };
              const cs = categoryStyles[note.category] || categoryStyles.feedback;

              return (
                <div
                  key={note.id}
                  className="p-4"
                  style={{
                    backgroundColor: cs.bg,
                    borderRadius: 'var(--radius-card)',
                    borderLeft: `4px solid ${cs.border}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/operadores/${note.operatorId}`}
                        style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)', textDecoration: 'none', color: 'var(--foreground)' }}
                      >
                        {note.operatorName}
                      </Link>
                      <span
                        className="px-2 py-0.5"
                        style={{
                          fontSize: 'var(--text-xs)',
                          borderRadius: 'var(--radius)',
                          backgroundColor: cs.border,
                          color: 'var(--primary-foreground)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        {cs.label}
                      </span>
                      {note.isResolved && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--success)', fontWeight: 'var(--font-weight-medium)' }}>
                          Resolvido
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                      {new Date(note.date).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: '1.5' }}>{note.note}</p>
                  {note.followUpDate && !note.isResolved && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--warning)', marginTop: '8px', fontWeight: 'var(--font-weight-medium)' }}>
                      <Clock size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                      Follow-up: {new Date(note.followUpDate).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modais */}
      <ApproveAuditModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onSuccess={() => { paEntity.refresh(); audEntity.refresh(); }}
        audit={selectedAudit}
      />
      <AddOperatorModal
        isOpen={showAddOperatorModal}
        onClose={() => setShowAddOperatorModal(false)}
      />
      <NotifyTeamModal
        isOpen={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
      />

      {/* Modal Nova Nota de Coaching */}
      {showNoteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowNoteModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
              padding: '24px',
              width: '100%',
              maxWidth: '480px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)' }}>Nova Nota de Coaching</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Operador</label>
              <select
                value={noteForm.operatorId}
                onChange={e => setNoteForm(f => ({ ...f, operatorId: e.target.value }))}
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--input)',
                  color: 'var(--foreground)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                }}
              >
                {myOperators.map(op => (
                  <option key={op.id} value={op.id}>{op.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Categoria</label>
              <select
                value={noteForm.category}
                onChange={e => setNoteForm(f => ({ ...f, category: e.target.value }))}
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--input)',
                  color: 'var(--foreground)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                }}
              >
                <option value="feedback">Feedback</option>
                <option value="recognition">Reconhecimento</option>
                <option value="action_plan">Plano de Ação</option>
                <option value="warning">Advertência</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Nota</label>
              <textarea
                rows={4}
                placeholder="Descreva a nota de coaching..."
                value={noteForm.note}
                onChange={e => setNoteForm(f => ({ ...f, note: e.target.value }))}
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--input)',
                  color: 'var(--foreground)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>Data de Acompanhamento (opcional)</label>
              <input
                type="date"
                value={noteForm.followUpDate}
                onChange={e => setNoteForm(f => ({ ...f, followUpDate: e.target.value }))}
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--input)',
                  color: 'var(--foreground)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNoteModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 'var(--radius-button)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'transparent',
                  color: 'var(--foreground)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-family-inter)',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!noteForm.operatorId || !noteForm.note.trim()) {
                    toast.error('Selecione um operador e escreva a nota.');
                    return;
                  }
                  const op = myOperators.find(o => o.id === noteForm.operatorId);
                  await coachingNotesEntity.add({
                    supervisorId: effectiveSupervisorId,
                    operatorId: noteForm.operatorId,
                    operatorName: op?.name || '',
                    date: new Date().toISOString(),
                    note: noteForm.note.trim(),
                    category: noteForm.category,
                    isResolved: false,
                    ...(noteForm.followUpDate ? { followUpDate: noteForm.followUpDate } : {}),
                  } as any);
                  toast.success('Nota de coaching salva.');
                  setShowNoteModal(false);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 'var(--radius-button)',
                  border: 'none',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  fontFamily: 'var(--font-family-inter)',
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
